import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, finalize, of, share, startWith, tap, throwError } from 'rxjs';
import { RequestCache } from 'src/app/core/services/cache/request-cache.service';
import { AnyApiPath, urlToRegExp } from '../services/api/ix-rest-api';

const CACHEABLE_RESOURCE_ALLOW_LIST: AnyApiPath[] = [
  '/api/eqhub/teknos/{tekno}',
  '/api/users/configuration',
  '/api/classifications/defaultvalues',
];
const CACHEABLE_RESOURCE_PATTERNS = CACHEABLE_RESOURCE_ALLOW_LIST.map(urlToRegExp);
const DISABLE_CACHE_VALUES = ['no-cache', 'no-store'] as const;

// Cache for in-flight requests to prevent duplicates
const inflightRequestsCache = new Map<string, Observable<HttpEvent<unknown>>>();

// Inspired by https://github.com/angular/angular/blob/f8d5b8464ba021d49d54a1cb49b8914336d9fb8e/aio/content/examples/http/src/app/http-interceptors/caching-interceptor.ts

/**
 * HTTP caching interceptor that prevents duplicate requests and caches responses.
 *
 * Features:
 * - Returns cached responses immediately when available
 * - Prevents multiple simultaneous requests for the same resource
 * - Supports cache-then-refresh with 'x-refresh' header
 * - Respects cache-control headers to disable caching
 */

export function cachingInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  if (!getIsCacheable(req)) {
    return next(req);
  }
  const cache = inject(RequestCache);
  const cachedResponse = safeCacheGet(cache, req);
  const requestKey = getRequestKey(req);

  // cache-then-refresh
  if (req.headers.get('x-refresh')) {
    const results$ = getOrCreateRequest(requestKey, req, next, cache);
    return cachedResponse ? results$.pipe(startWith(cachedResponse)) : results$;
  }

  return cachedResponse ? of(cachedResponse) : getOrCreateRequest(requestKey, req, next, cache);
}

/**
 * Gets or creates a request observable for the given request key.
 * Prevents duplicate requests for the same resource.
 */
const getOrCreateRequest = (
  requestKey: string,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  cache: RequestCache,
): Observable<HttpEvent<unknown>> => {
  const inflightRequest$ = inflightRequestsCache.get(requestKey);
  if (inflightRequest$) {
    return inflightRequest$;
  }

  const request$ = sendRequest(req, next, cache).pipe(
    finalize(() => inflightRequestsCache.delete(requestKey)),
    share(),
    catchError(error => {
      inflightRequestsCache.delete(requestKey);
      return throwError(() => error);
    }),
  );

  inflightRequestsCache.set(requestKey, request$);
  return request$;
};

const getIsCacheable = (req: HttpRequest<unknown>): boolean => {
  return getIsNotDisabledByHeader(req) && getIsHttpGET(req) && getIsUrlCacheable(req.url);
};

const getIsNotDisabledByHeader = (req: HttpRequest<unknown>): boolean => {
  return !getIsDisableCacheHeaderSet(req);
};

const getIsDisableCacheHeaderSet = (req: HttpRequest<unknown>): boolean => {
  const cacheControl = req.headers.get('Cache-Control')?.toLowerCase();

  if (!cacheControl) {
    return false;
  }

  return DISABLE_CACHE_VALUES.some(disableValue => cacheControl.includes(disableValue));
};

const getIsHttpGET = (req: HttpRequest<unknown>): boolean => {
  return req.method === 'GET';
};

const getIsUrlCacheable = (url: string): boolean => {
  return CACHEABLE_RESOURCE_PATTERNS.some(pattern => pattern.test(url));
};

const getRequestKey = (req: HttpRequest<unknown>): string => {
  return `${req.method}:${req.urlWithParams}`;
};

const sendRequest = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  cache: RequestCache,
): Observable<HttpEvent<unknown>> => {
  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse && event.ok) {
        safeCachePut(cache, req, event);
      }
    }),
  );
};

/*
  Caching errors should not affect the main request handling - so we use try-catch.
*/
const safeCachePut = (
  cache: RequestCache,
  req: HttpRequest<unknown>,
  response: HttpResponse<unknown>,
): void => {
  try {
    cache.put(req, response);
  } catch (error) {
    console.warn(`Failed to cache response for ${req.urlWithParams}:`, error);
  }
};

const safeCacheGet = (cache: RequestCache, req: HttpRequest<unknown>): HttpResponse<unknown> | undefined => {
  try {
    return cache.get(req);
  } catch (error) {
    console.warn(`Failed to get cached response for ${req.urlWithParams}:`, error);
    return undefined;
  }
};
